import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, X, ExternalLink, Trash2, Mail, Phone } from 'lucide-react';
import { api } from '../lib/api';

export const KINDS = [
    ['', 'All'], ['order', 'Orders'], ['rental', 'Rentals'], ['repair', 'Repairs'],
    ['quote', 'Quotes'], ['message', 'Messages'], ['finder', 'Finder leads'], ['training', 'Training'],
];
const KIND_LABEL = Object.fromEntries(KINDS);
const STATUSES = [['', 'Any status'], ['new', 'New'], ['in_progress', 'In progress'], ['done', 'Done']];
const STATUS_STYLE = { new: 'bg-brand-primary text-black', in_progress: 'bg-amber-500 text-black', done: 'bg-white/10 text-white/50' };
const TAB_LABEL = { orders: 'Orders', rentals: 'Rentals', 'service-requests': 'Service Requests', contacts: 'Contacts', 'finder-leads': 'Finder Leads', training: 'Training' };

const StatusButtons = ({ item, onChange }) => (
    <div className="flex items-center gap-2" data-testid={`inbox-status-group-${item.id}`}>
        <span className={`w-2 h-2 rounded-full ${item.stage === 'new' ? 'bg-brand-primary' : item.stage === 'in_progress' ? 'bg-amber-500' : 'bg-white/30'}`} data-testid={`inbox-stage-${item.id}`} data-stage={item.stage} />
        {item.editable ? (
            <select value={item.status} onChange={(e) => onChange(item, e.target.value)} data-testid={`inbox-status-select-${item.id}`}
                className={`px-2 py-1 text-xs label-caps outline-none cursor-pointer ${STATUS_STYLE[item.stage]}`}>
                {item.status_options.map(([v, l]) => <option key={v} value={v} className="bg-brand-bg text-white">{l}</option>)}
            </select>
        ) : (
            <span className={`px-2.5 py-1 text-xs label-caps ${STATUS_STYLE[item.stage]}`} title="Change order status in the Orders tab" data-testid={`inbox-status-pill-${item.id}`}>
                {item.status_label}
            </span>
        )}
    </div>
);

const Notes = ({ item, onSaved }) => {
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const run = async (fn) => {
        setBusy(true);
        try { onSaved((await fn()).data); return true; } catch { toast.error('Could not save note'); return false; } finally { setBusy(false); }
    };
    const add = async () => {
        if (!text.trim() || busy) return;
        if (await run(() => api.post(`/admin/inbox/${item.kind}/${item.id}/notes`, { text }))) setText('');
    };
    const del = (n) => run(() => api.delete(`/admin/inbox/${item.kind}/${item.id}/notes/${n.id}`));
    return (
        <div data-testid="inbox-notes">
            <div className="label-caps text-white/60 mb-2">Internal notes</div>
            <div className="space-y-2 mb-3">
                {item.notes.length === 0 && <div className="text-sm text-white/40">No notes yet.</div>}
                {item.notes.map((n) => (
                    <div key={n.id} className="bg-white/5 p-3 text-sm flex justify-between gap-3" data-testid={`inbox-note-${n.id}`}>
                        <div><div className="text-white whitespace-pre-wrap">{n.text}</div>
                            <div className="text-white/40 text-xs mt-1">{n.by} · {new Date(n.at).toLocaleString()}</div></div>
                        <button onClick={() => del(n)} className="text-white/40 hover:text-red-400" data-testid={`inbox-note-delete-${n.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
            </div>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Called back, sent pricing…"
                className="w-full bg-brand-bg border border-white/10 focus:border-brand-primary text-white p-3 outline-none text-sm" data-testid="inbox-note-input" />
            <button onClick={add} disabled={busy} className="mt-2 bg-brand-primary text-black label-caps px-4 py-2 hover:brightness-110 transition-[filter] disabled:opacity-50" data-testid="inbox-note-add">{busy ? 'Saving…' : 'Add note'}</button>
        </div>
    );
};

const Detail = ({ item, onClose, onStatus, onMeta, onOpenTab }) => (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="inbox-detail">
        <div className="w-full max-w-xl h-full overflow-y-auto bg-brand-bg border-l border-white/10 p-6 space-y-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start gap-4">
                <div>
                    <div className="label-caps text-brand-primary">{KIND_LABEL[item.kind]} {item.ref && `· ${item.ref}`}</div>
                    <h3 className="font-display font-black uppercase text-2xl mt-1" data-testid="inbox-detail-name">{item.name || item.email}</h3>
                    <div className="text-white/50 text-sm">{[item.company, item.created_at && new Date(item.created_at).toLocaleString()].filter(Boolean).join(' · ')}</div>
                </div>
                <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="inbox-detail-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
                {item.email && <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1 text-brand-primary" data-testid="inbox-detail-email"><Mail className="h-4 w-4" />{item.email}</a>}
                {item.phone && <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 text-brand-primary" data-testid="inbox-detail-phone"><Phone className="h-4 w-4" />{item.phone}</a>}
            </div>
            <StatusButtons item={item} onChange={onStatus} />
            {!item.editable && <p className="text-xs text-white/50 -mt-4" data-testid="inbox-order-status-hint">Order status (and the customer shipping emails) is changed in the Orders tab.</p>}
            <dl className="border border-white/10 divide-y divide-white/10 text-sm" data-testid="inbox-detail-fields">
                {item.details.map((d) => (
                    <div key={d.label} className="grid grid-cols-3 gap-3 p-3">
                        <dt className="text-white/50">{d.label}</dt><dd className="col-span-2 text-white whitespace-pre-wrap break-words">{d.value}</dd>
                    </div>
                ))}
            </dl>
            <Notes item={item} onSaved={onMeta} />
            <button onClick={() => onOpenTab(item.tab)} className="inline-flex items-center gap-2 label-caps border border-white/20 hover:bg-white/10 transition-colors px-4 py-2" data-testid="inbox-open-original">
                <ExternalLink className="h-4 w-4" /> Open in {TAB_LABEL[item.tab]}
            </button>
        </div>
    </div>
);

const Row = ({ item, onOpen, onStatus }) => (
    <div className={`p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center cursor-pointer hover:bg-white/5 transition-colors ${item.stage === 'new' ? 'border-l-2 border-brand-primary' : ''}`}
        onClick={() => onOpen(item)} data-testid={`inbox-row-${item.id}`}>
        <div className="md:col-span-2"><span className="label-caps text-xs text-brand-primary">{KIND_LABEL[item.kind]}</span>
            <div className="text-xs text-white/40">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</div></div>
        <div className="md:col-span-3 min-w-0"><div className={`truncate ${item.stage === 'new' ? 'font-bold text-white' : 'text-white/80'}`}>{item.name || '—'}</div>
            <div className="text-xs text-white/50 truncate">{item.email}{item.company && ` · ${item.company}`}</div></div>
        <div className="md:col-span-4 text-sm text-white/60 truncate">{item.summary}</div>
        <div className="md:col-span-3 flex md:justify-end gap-2 items-center" onClick={(e) => e.stopPropagation()}>
            {item.notes.length > 0 && <span className="text-xs text-white/40">{item.notes.length} note{item.notes.length > 1 ? 's' : ''}</span>}
            <StatusButtons item={item} onChange={onStatus} />
        </div>
    </div>
);

const InboxAdmin = ({ onOpenTab, onChanged }) => {
    const [data, setData] = useState({ items: [], counts: {} });
    const [kind, setKind] = useState('');
    const [status, setStatus] = useState('');
    const [q, setQ] = useState('');
    const [openKey, setOpenKey] = useState(null);
    const load = useCallback(() => api.get('/admin/inbox', { params: { kind, status, q } }).then((r) => setData(r.data)), [kind, status, q]);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

    const applyMeta = (meta) => setData((d) => ({ ...d, items: d.items.map((i) => (i.key === meta.key ? { ...i, ...meta } : i)) }));
    const changeStatus = async (item, v) => {
        try {
            applyMeta((await api.put(`/admin/inbox/${item.kind}/${item.id}`, { status: v })).data);
            load();
            toast.success('Status updated');
            onChanged();
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not update status');
        }
    };
    const open = data.items.find((i) => i.key === openKey);

    return (
        <div data-testid="admin-inbox">
            <h2 className="font-display font-black uppercase text-3xl mb-2">Lead inbox</h2>
            <p className="text-white/50 text-sm mb-6">Every order, rental, repair, quote, message and Finder lead in one place. <span data-testid="inbox-new-count">{data.counts.new || 0}</span> waiting.</p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="inbox-kind-filters">
                {KINDS.map(([v, l]) => (
                    <button key={v || 'all'} onClick={() => setKind(v)} data-testid={`inbox-kind-${v || 'all'}`}
                        className={`px-3 py-1.5 label-caps text-xs transition-colors ${kind === v ? 'bg-brand-primary text-black' : 'border border-white/10 text-white/60 hover:text-white'}`}>
                        {l} <span className="opacity-60">{v ? data.counts[v] || 0 : data.counts.all || 0}</span>
                    </button>
                ))}
            </div>
            <div className="flex flex-col md:flex-row gap-3 mb-6">
                <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company or order #"
                        className="w-full bg-brand-bg border border-white/10 focus:border-brand-primary text-white py-3 pl-9 pr-3 outline-none" data-testid="inbox-search" /></div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-brand-bg border border-white/10 text-white p-3 outline-none" data-testid="inbox-status-filter">
                    {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            </div>
            <div className="border border-white/10 divide-y divide-white/10" data-testid="inbox-list">
                {data.items.length === 0 && <div className="p-6 text-white/50" data-testid="inbox-empty">Nothing here — you&apos;re all caught up.</div>}
                {data.items.map((i) => <Row key={i.key} item={i} onOpen={(it) => setOpenKey(it.key)} onStatus={changeStatus} />)}
            </div>
            {open && <Detail item={open} onClose={() => setOpenKey(null)} onStatus={changeStatus} onMeta={applyMeta} onOpenTab={onOpenTab} />}
        </div>
    );
};

export default InboxAdmin;
